/** @fileoverview Resolves the effective dates of an OData source into the fixed days a query sends. */

import type {
  FabrikEffectiveDates,
  FabrikEffectiveDay,
  FabrikRecordKeyRefValue,
} from '../../types/apps';

/* An SAP date such as `/Date(1760486400000)/`, with an optional offset. */
const SAP_DATE = /^\/Date\((-?\d+)(?:[+-]\d{4})?\)\/$/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Turns a date value into a `YYYY-MM-DD` day: the first 10 characters of a date or date-time
 * text, or the UTC day of an SAP date. Gives undefined for an empty or unreadable value.
 * @param value the date value read from a record, a globals field, or an input
 */
export const dayOf = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value) {
    return undefined;
  }
  const sapDate = SAP_DATE.exec(value);
  if (sapDate) {
    const date = new Date(Number(sapDate[1]));
    return Number.isFinite(date.getTime()) ? date.toISOString().slice(0, 10) : undefined;
  }
  const day = value.slice(0, 10);
  return ISO_DAY.test(day) ? day : undefined;
};

/**
 * Resolves the effective dates of a source into fixed days. An empty ref value leaves its day
 * out; an `asOf` with no day, or a range with no end, gives undefined, so no date is sent.
 * @param effectiveDates the source's effective dates, when it has them
 * @param readRef reads the value of a ref, the same way row filter values are read
 */
export const resolveEffectiveDates = (
  effectiveDates: FabrikEffectiveDates | undefined,
  readRef: (ref: FabrikRecordKeyRefValue) => unknown
): FabrikEffectiveDates | undefined => {
  if (!effectiveDates) {
    return undefined;
  }
  /**
   * Resolves one day of the effective dates.
   * @param day a fixed day or a ref
   */
  const resolveDay = (day: FabrikEffectiveDay | undefined) =>
    day === undefined ? undefined : dayOf(typeof day === 'string' ? day : readRef(day));
  if ('asOf' in effectiveDates) {
    const asOf = resolveDay(effectiveDates.asOf);
    return asOf ? { asOf } : undefined;
  }
  const from = resolveDay(effectiveDates.from);
  const to = resolveDay(effectiveDates.to);
  return from || to ? { ...(from ? { from } : {}), ...(to ? { to } : {}) } : undefined;
};

/**
 * Gives the query body fields of resolved effective dates: `asOfDate`, or `fromDate` and
 * `toDate`. Days still given as refs are left out, since only fixed days can be sent.
 * @param effectiveDates the resolved effective dates, when the source has them
 */
export const effectiveDateQueryFields = (
  effectiveDates: FabrikEffectiveDates | undefined
): { asOfDate?: string; fromDate?: string; toDate?: string } => {
  if (!effectiveDates) {
    return {};
  }
  if ('asOf' in effectiveDates) {
    const { asOf } = effectiveDates;
    return typeof asOf === 'string' ? { asOfDate: asOf } : {};
  }
  const { from, to } = effectiveDates;
  return {
    ...(typeof from === 'string' ? { fromDate: from } : {}),
    ...(typeof to === 'string' ? { toDate: to } : {}),
  };
};
